/* iter95 — Security page for gate agents and admin.
 * Two tabs: Visiteurs (register/checkout) and Incidents (log + resolve).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { ShieldCheck, UserPlus, SignOut, Warning, CheckCircle, CalendarCheck } from '@phosphor-icons/react';

const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  : '—';

const Security = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isVigil = user?.role === 'vigil';
  const [tab, setTab] = useState('visitors');
  const [visitors, setVisitors] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(false);

  // Visitor form
  const [vForm, setVForm] = useState({ name: '', purpose: '', host_name: '', phone: '', badge_no: '', id_number: '', company: '' });
  // Incident form
  const [iForm, setIForm] = useState({ kind: 'behavior', severity: 'low', description: '', student_name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, i] = await Promise.all([
        api.get('/security/visitors', { params: onlyActive ? { only_active: 1 } : {} }),
        api.get('/security/incidents', { params: onlyOpen ? { only_open: 1 } : {} }),
      ]);
      setVisitors(v.data.items || []);
      setIncidents(i.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, [onlyActive, onlyOpen]);

  useEffect(() => { load(); }, [load]);

  const addVisitor = async () => {
    if (!vForm.name.trim() || !vForm.purpose.trim()) {
      toast.error('Nom et motif requis'); return;
    }
    try {
      await api.post('/security/visitors', vForm);
      setVForm({ name: '', purpose: '', host_name: '', phone: '', badge_no: '', id_number: '', company: '' });
      toast.success('Visiteur enregistré');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const checkoutVisitor = async (id) => {
    try {
      await api.patch(`/security/visitors/${id}/checkout`);
      toast.success('Sortie enregistrée');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const addIncident = async () => {
    if (!iForm.description.trim()) { toast.error('Description requise'); return; }
    try {
      await api.post('/security/incidents', iForm);
      setIForm({ kind: 'behavior', severity: 'low', description: '', student_name: '' });
      toast.success('Incident enregistré');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const resolveIncident = async (id) => {
    // eslint-disable-next-line no-alert
    const resolution = window.prompt('Description de la résolution :');
    if (!resolution) return;
    try {
      await api.patch(`/security/incidents/${id}/resolve`, { resolution });
      toast.success('Incident résolu');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className={isVigil ? "min-h-screen bg-slate-50" : ""}
      data-testid="security-page">
      {/* iter96 — Standalone header for the Vigil (no sidebar) */}
      {isVigil && (
        <div className="bg-indigo-600 text-white px-4 py-3 flex items-center justify-between shadow-md sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} weight="duotone" />
            <div>
              <div className="font-outfit font-bold text-lg leading-tight">Poste de garde</div>
              <div className="text-[11px] opacity-90">{user?.email || user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm"
              onClick={() => navigate('/my-absences')}
              data-testid="vigil-my-absences"
              className="bg-white/20 hover:bg-white/30 border-0 text-white">
              <CalendarCheck size={16} className="mr-1" /> Mes absences
            </Button>
            <Button variant="secondary" size="sm"
              onClick={() => logout && logout()}
              data-testid="vigil-logout"
              className="bg-white/20 hover:bg-white/30 border-0 text-white">
              <SignOut size={16} className="mr-1" /> Fermer session
            </Button>
          </div>
        </div>
      )}
      <div className={`p-6 max-w-5xl mx-auto space-y-4 ${isVigil ? 'pt-4' : ''}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
            <ShieldCheck size={26} className="text-indigo-600" weight="duotone" />
            Sécurité
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Registre visiteurs et journal d'incidents.
          </p>
        </div>
        {/* iter96 — PDF exports of the visitor register */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"
            data-testid="export-pdf-today"
            onClick={() => window.open(
              `${process.env.REACT_APP_BACKEND_URL}/api/security/visitors/export.pdf`,
              '_blank')}>
            📄 PDF jour
          </Button>
          <Button variant="outline" size="sm"
            data-testid="export-pdf-month"
            onClick={() => {
              const m = new Date().toISOString().slice(0, 7);
              window.open(
                `${process.env.REACT_APP_BACKEND_URL}/api/security/visitors/export.pdf?month=${m}`,
                '_blank');
            }}>
            📄 PDF mois
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-[#E2E8F0]">
        <button className={`px-4 py-2 text-sm font-semibold ${tab === 'visitors' ? 'text-indigo-700 border-b-2 border-indigo-600' : 'text-[#64748B]'}`}
          onClick={() => setTab('visitors')} data-testid="tab-visitors">
          Visiteurs ({visitors.length})
        </button>
        <button className={`px-4 py-2 text-sm font-semibold ${tab === 'incidents' ? 'text-indigo-700 border-b-2 border-indigo-600' : 'text-[#64748B]'}`}
          onClick={() => setTab('incidents')} data-testid="tab-incidents">
          Incidents ({incidents.length})
        </button>
      </div>

      {tab === 'visitors' && (
        <>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <UserPlus size={18} className="text-indigo-600" weight="duotone" />
                Enregistrer un visiteur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Nom complet *" value={vForm.name}
                  onChange={(e) => setVForm({ ...vForm, name: e.target.value })}
                  data-testid="v-name" />
                <Input placeholder="Motif de visite *" value={vForm.purpose}
                  onChange={(e) => setVForm({ ...vForm, purpose: e.target.value })}
                  data-testid="v-purpose" />
                <Input placeholder="Personne visitée" value={vForm.host_name}
                  onChange={(e) => setVForm({ ...vForm, host_name: e.target.value })}
                  data-testid="v-host" />
                <Input placeholder="Téléphone" value={vForm.phone}
                  onChange={(e) => setVForm({ ...vForm, phone: e.target.value })}
                  data-testid="v-phone" />
                <Input placeholder="N° badge" value={vForm.badge_no}
                  onChange={(e) => setVForm({ ...vForm, badge_no: e.target.value })}
                  data-testid="v-badge" />
                <Input placeholder="Société / organisation" value={vForm.company}
                  onChange={(e) => setVForm({ ...vForm, company: e.target.value })}
                  data-testid="v-company" />
              </div>
              <Button onClick={addVisitor} className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="v-submit">
                Enregistrer l'entrée
              </Button>
            </CardContent>
          </Card>

          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
                data-testid="v-only-active" />
              Uniquement à l'intérieur
            </label>
            <span className="text-[#94A3B8]">·</span>
            <span className="text-[#94A3B8]">{loading ? 'Chargement…' : `${visitors.length} visiteur(s) aujourd'hui`}</span>
          </div>

          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[#64748B] text-[11px] uppercase">
                  <tr>
                    <th className="text-left py-2 px-3">Nom</th>
                    <th className="text-left py-2 px-3">Motif</th>
                    <th className="text-left py-2 px-3">Hôte</th>
                    <th className="text-left py-2 px-3">Badge</th>
                    <th className="text-center py-2 px-3">Entrée</th>
                    <th className="text-center py-2 px-3">Sortie</th>
                    <th className="text-center py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((v) => (
                    <tr key={v.id} className="border-t border-[#F1F5F9]"
                      data-testid={`v-row-${v.id}`}>
                      <td className="py-2 px-3 font-semibold">{v.name}</td>
                      <td className="py-2 px-3">{v.purpose}</td>
                      <td className="py-2 px-3 text-[#475569]">{v.host_name || '—'}</td>
                      <td className="py-2 px-3 text-[#475569]">{v.badge_no || '—'}</td>
                      <td className="py-2 px-3 text-center text-[#0F172A]">{fmtTime(v.entered_at)}</td>
                      <td className="py-2 px-3 text-center">
                        {v.left_at
                          ? <span className="text-emerald-700">{fmtTime(v.left_at)}</span>
                          : <span className="text-amber-600 font-bold">à l'intérieur</span>}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {!v.left_at && (
                          <Button size="sm" variant="outline"
                            onClick={() => checkoutVisitor(v.id)}
                            data-testid={`v-checkout-${v.id}`}>
                            <SignOut size={13} className="mr-1" /> Sortie
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!visitors.length && (
                    <tr><td colSpan={7} className="text-center py-8 text-[#94A3B8]">
                      Aucun visiteur.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'incidents' && (
        <>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Warning size={18} className="text-red-600" weight="duotone" />
                Signaler un incident
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <select value={iForm.kind}
                  onChange={(e) => setIForm({ ...iForm, kind: e.target.value })}
                  className="h-10 px-3 rounded-md border border-[#E2E8F0] text-sm"
                  data-testid="i-kind">
                  <option value="behavior">Comportement</option>
                  <option value="medical">Médical</option>
                  <option value="security">Sécurité</option>
                  <option value="other">Autre</option>
                </select>
                <select value={iForm.severity}
                  onChange={(e) => setIForm({ ...iForm, severity: e.target.value })}
                  className="h-10 px-3 rounded-md border border-[#E2E8F0] text-sm"
                  data-testid="i-severity">
                  <option value="low">Faible</option>
                  <option value="medium">Moyen</option>
                  <option value="high">Grave</option>
                </select>
                <Input placeholder="Nom de l'élève (optionnel)"
                  value={iForm.student_name}
                  onChange={(e) => setIForm({ ...iForm, student_name: e.target.value })}
                  data-testid="i-student" />
              </div>
              <Textarea placeholder="Description détaillée *"
                value={iForm.description} rows={3}
                onChange={(e) => setIForm({ ...iForm, description: e.target.value })}
                data-testid="i-desc" />
              <Button onClick={addIncident} className="bg-red-600 hover:bg-red-700"
                data-testid="i-submit">
                Enregistrer l'incident
              </Button>
            </CardContent>
          </Card>

          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={onlyOpen}
                onChange={(e) => setOnlyOpen(e.target.checked)}
                data-testid="i-only-open" />
              Uniquement ouverts
            </label>
          </div>

          <div className="space-y-2">
            {incidents.map((i) => {
              const sev = { low: 'border-emerald-300 bg-emerald-50', medium: 'border-amber-300 bg-amber-50', high: 'border-red-300 bg-red-50' }[i.severity];
              return (
                <Card key={i.id} className={`border shadow-none ${sev}`}
                  data-testid={`i-row-${i.id}`}>
                  <CardContent className="p-3 flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-wider text-[#64748B] mb-1">
                        {i.kind} · {i.severity} · {fmtTime(i.created_at)}
                        {i.student_name ? ` · ${i.student_name}` : ''}
                      </div>
                      <div className="text-sm text-[#0F172A]">{i.description}</div>
                      {i.status === 'resolved' && (
                        <div className="text-xs text-emerald-700 mt-1">
                          ✓ Résolu — {i.resolution}
                        </div>
                      )}
                    </div>
                    {i.status === 'open' && (
                      <Button size="sm" variant="outline"
                        onClick={() => resolveIncident(i.id)}
                        data-testid={`i-resolve-${i.id}`}>
                        <CheckCircle size={13} className="mr-1" /> Résoudre
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {!incidents.length && (
              <p className="text-center py-8 text-[#94A3B8] text-sm">
                Aucun incident.
              </p>
            )}
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default Security;
